/**
 * Number a bibliography so a numeric citation can be resolved.
 *
 * The rule, in order of trust:
 *   1. An ordinal an entry carries itself — the `ordinal` field, or a literal prefix on its
 *      text (`[1] …`, `(1) …`, `1. …`, `1) …`, `1- …`) — is the only ordinal the entry gets.
 *   2. List position stands in for the ordinal only when the caller asks (`positional: true`)
 *      and either no entry carries an ordinal at all, or every ordinal that is carried agrees
 *      with its entry's position (so the missing ones are an extraction artefact, not a
 *      different scheme). An entry without an id still consumes its position, or every later
 *      entry would be numbered one low and every citation to it would point at a real but
 *      wrong reference.
 *   3. Anything else refuses: two entries claiming one ordinal, or ordinals on some entries but
 *      not others that do not line up with position. A refused index resolves nothing; a
 *      citation left unlinked is visible, one linked to the wrong reference is not.
 *
 * Pass the entries of ONE bibliography, in the order they are printed. When a document has
 * more than one reference-bearing region, position is not trustworthy — do not opt in.
 */

export interface OrdinalEntry {
  /** Empty when the entry cannot be a link target; it still holds its position. */
  id: string;
  /** The bibliography line, when known. */
  text?: string;
  /** An ordinal the caller already knows for this entry. */
  ordinal?: number;
}

export interface OrdinalConflict {
  ordinal: number;
  ids: string[];
}

export interface OrdinalIndex {
  byOrdinal: Map<number, string>;
  /** `ok`: `byOrdinal` may be used. `none`: nothing to number by. `ambiguous`: refused. */
  status: 'ok' | 'none' | 'ambiguous';
  /** Where the ordinals came from. */
  source: 'literal' | 'positional' | 'none';
  reason: string;
  conflicts: OrdinalConflict[];
  /** How many entries were given, and how many carried an ordinal of their own. */
  entries: number;
  carried: number;
}

export interface OrdinalIndexOptions {
  /** Let list position stand in for a missing ordinal, under the rule above. */
  positional?: boolean;
}

/** The five prefix shapes a numbered bibliography line may carry. */
export const ORDINAL_PREFIX_RE = /^\s*(?:\[(\d+)\]|\((\d+)\)|(\d+)[.)]|(\d+)[-–])\s*/u;

/** The ordinal a bibliography line carries as a literal prefix, or null. */
export function literalOrdinal(text: string | undefined): number | null {
  if (typeof text !== 'string') return null;
  const m = ORDINAL_PREFIX_RE.exec(text);
  if (!m) return null;
  const n = parseInt(m[1] ?? m[2] ?? m[3] ?? m[4], 10);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function carriedOrdinal(entry: OrdinalEntry): number | null {
  if (typeof entry.ordinal === 'number' && Number.isInteger(entry.ordinal) && entry.ordinal > 0) return entry.ordinal;
  return literalOrdinal(entry.text);
}

function positionalIndex(entries: OrdinalEntry[], reason: string): OrdinalIndex {
  const byOrdinal = new Map<number, string>();
  entries.forEach((e, i) => {
    if (e.id) byOrdinal.set(i + 1, e.id);
  });
  return { byOrdinal, status: 'ok', source: 'positional', reason, conflicts: [], entries: entries.length, carried: 0 };
}

export function buildOrdinalIndex(entries: OrdinalEntry[], opts: OrdinalIndexOptions = {}): OrdinalIndex {
  const n = entries.length;
  const claims = entries.map((e, i) => ({ id: e.id ?? '', ordinal: carriedOrdinal(e), position: i + 1 }));
  const carried = claims.filter((c) => c.ordinal !== null);
  const base = { conflicts: [] as OrdinalConflict[], entries: n, carried: carried.length };

  if (n === 0) return { byOrdinal: new Map(), status: 'none', source: 'none', reason: 'no entries', ...base };

  if (carried.length === 0) {
    if (opts.positional) {
      return { ...positionalIndex(entries, 'no entry carries an ordinal; list position used at the caller\'s request'), carried: 0 };
    }
    return { byOrdinal: new Map(), status: 'none', source: 'none', reason: 'no entry carries an ordinal, and list position was not requested', ...base };
  }

  const byClaim = new Map<number, string[]>();
  for (const c of carried) {
    const ids = byClaim.get(c.ordinal!) ?? [];
    ids.push(c.id);
    byClaim.set(c.ordinal!, ids);
  }
  const conflicts = [...byClaim.entries()].filter(([, ids]) => ids.length > 1).map(([ordinal, ids]) => ({ ordinal, ids }));
  if (conflicts.length > 0) {
    return {
      byOrdinal: new Map(),
      status: 'ambiguous',
      source: 'none',
      reason: `more than one entry claims ordinal ${conflicts.map((c) => c.ordinal).join(', ')}`,
      ...base,
      conflicts,
    };
  }

  if (carried.length < n) {
    const disagree = carried.filter((c) => c.ordinal !== c.position);
    if (opts.positional && disagree.length === 0) {
      return {
        ...positionalIndex(
          entries,
          `only ${carried.length} of ${n} entries carry an ordinal and every one agrees with its position; list position used for the rest`,
        ),
        carried: carried.length,
      };
    }
    const why = opts.positional
      ? `${disagree.length} of them disagree with their position (a different numbering scheme)`
      : 'list position was not requested';
    return {
      byOrdinal: new Map(),
      status: 'ambiguous',
      source: 'none',
      reason: `only ${carried.length} of ${n} entries carry an ordinal, and ${why}`,
      ...base,
    };
  }

  const byOrdinal = new Map<number, string>();
  for (const c of carried) if (c.id) byOrdinal.set(c.ordinal!, c.id);
  return { byOrdinal, status: 'ok', source: 'literal', reason: 'every entry carries its own ordinal', ...base };
}
